import { useState, useEffect } from "react";
import PatientSidebar from "../../components/dashboard/PatientSidebar";
import LogoSection from "../../components/ui/LogoSection";
import StudyController from "@/network/controllers/study.controller";
import { useSession } from "@/hooks/useSession";
import { useNavigate } from "react-router-dom";

export default function PatientDashboard({ onSignOut = () => {} }) {
  const [showSidebar, setShowSidebar] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useSession();
  const [patientData, setPatientData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPatientDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const patientId = user?.userId;
        console.log("=== patientId from session:", patientId);

        if (!patientId) {
          setError("Patient ID not found in session.");
          setLoading(false);
          return;
        }

        const response = await StudyController.getPatientConsentDetails(
          patientId
        );

        if (response && response.success === false) {
          setError(response.message || "Failed to fetch patient details.");
        } else if (response) {
          setPatientData(response);

          if (!response.consent_id) {
            navigate("/patient-consent-signature", { replace: true });
          } else if (response.participant_consent_status === "revoked") {
            navigate("/patient-qr-code-error", { replace: true });
          } else if (
            response.participant_consent_status !== "validated" ||
            response.investigator_consent_status !== "validated"
          ) {
            navigate("/patient-consent-signature", { replace: true });
          } else {
            navigate("/patient-qr-code", {
              replace: true,
              state: { patientData: response },
            });
          }
        } else {
          setError("No data received for patient details.");
        }
      } catch (err) {
        console.error("Error fetching patient details:", err);
        setError(err.message || "An unexpected error occurred.");
      } finally {
        setLoading(false);
      }
    };

    fetchPatientDetails();
  }, [user, navigate]);

  const toggleSidebar = () => setShowSidebar(!showSidebar);

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <p>Loading patient dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center text-red-500">
        <p>Error: {error}</p>
      </div>
    );
  }

  return (
    <div className="flex h-screen relative">
      <button
        onClick={toggleSidebar}
        className="md:hidden fixed top-4 left-4 z-50 bg-white p-2 rounded-md shadow-md"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      </button>

      <div
        className={`${
          showSidebar ? "block" : "hidden"
        } md:block fixed md:relative z-40 h-full`}
      >
        <PatientSidebar
          patientName={`${patientData?.patient_firstname || ""} ${
            patientData?.patient_lastname || ""
          }`.trim()}
          onSignOut={onSignOut}
          showSignature={true}
          showQRCode={false}
        />
      </div>

      {showSidebar && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setShowSidebar(false)}
        ></div>
      )}

      <div className="flex-1 overflow-auto bg-dashboard-bg">
        <main className="p-4 md:p-6">
          <div className="bg-dashboard-bg1 p-4 md:p-6 rounded-xl">
            <h2 className="text-xl font-semibold mb-4">Redirecting...</h2>
          </div>
        </main>
      </div>
    </div>
  );
}
